/* eslint-disable import/no-named-as-default */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
'use client'

import { useEffect, useState } from 'react'

import { CampaignEvent } from '@lib/analytics'
import Loader from '@components/common/loader'
import { initRedirect } from './redirect-server'
import { linkedInTrack } from '@lib/linkedin'
import posthog from 'posthog-js'
import { sendGTMEvent } from '@next/third-parties/google'

declare global {
  interface Window {
    dataLayer?: Object[]
    _linkedin_data_partner_ids?: number[]
  }
}

interface GTMEvent {
  'gtm.start'?: number
  'gtm.uniqueEventId'?: number
}

export default function RedirectAfterAnalytics({
  linkedInConversionId,
  redirectUrl,
}: {
  linkedInConversionId?: number | null
  redirectUrl: string
}) {
  const [check, setCheck] = useState(true)
  const [gtmLoaded, setGtmLoaded] = useState(false)
  const [liLoaded, setLiLoaded] = useState(false)
  const [eventsSubmitted, setEventsSubmitted] = useState(false)

  // 1. Ensure analytics event data is submitted.
  useEffect(() => {
    if (!eventsSubmitted && linkedInConversionId) {
      linkedInTrack(linkedInConversionId)
      posthog.capture(CampaignEvent.LinkedinConversion, {
        linkedInConversionId,
        redirectUrl,
      })
      sendGTMEvent({ event: CampaignEvent.LinkedinConversion })
      setEventsSubmitted(true)
    }
  }, [linkedInConversionId])

  // 2. Check if GTM and LinkedIn scripts external scripts have executed
  useEffect(() => {
    if (check) {
      setGtmLoaded(isGtmLoaded)
      setLiLoaded(isLiLoaded)
      setCheck(false)

      if (!gtmLoaded || !liLoaded) {
        // Wait for network idle (Google Analytics/GTM & LinkedIn event collection requests) before checking again
        // 100ms timeout to avoid smashing the browsers event loop
        const timeout = 100

        // Safari does not support requestIdleCallback. See https://developer.mozilla.org/en-US/docs/Web/API/Window/requestIdleCallback#browser_compatibility
        if (window.requestIdleCallback === undefined) {
          setTimeout(() => {
            setCheck(true)
          }, timeout)
        } else {
          requestIdleCallback(
            () => {
              setCheck(true)
            },
            { timeout },
          )
        }
      }
    }
  }, [check, eventsSubmitted])

  // 3. Redirect after all events have been submitted and external scripts have loaded
  useEffect(() => {
    if (eventsSubmitted && gtmLoaded && liLoaded) {
      initRedirect(redirectUrl)
    }
  }, [redirectUrl, eventsSubmitted, gtmLoaded, liLoaded])

  return (
    <div className='mx-auto'>
      <Loader className='h-20 w-20 fill-black' />
    </div>
  )
}

function isGtmLoaded() {
  window.dataLayer = window.dataLayer ?? []

  if (window.dataLayer.length > 0) {
    const gtmStartedEvent: GTMEvent | undefined = window.dataLayer.find(
      (element: GTMEvent) => element['gtm.start'] !== undefined,
    )

    // Return true is GTM inline config has executed and main GTM js has loaded. (false return after full page load likely caused by AdBlock, NoScript, URL blocking etc.)
    return Number.isInteger(gtmStartedEvent?.['gtm.uniqueEventId']) // GTM fully loaded and working
  }

  return false // No events in dataLayer
}

function isLiLoaded() {
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids ?? [] // Wait until linkedin script has loaded
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const queue = window.lintrk?.q ?? [] // Wait until linkedin script has published events from the queue

  return window._linkedin_data_partner_ids.length > 1 && queue.length === 0
}
